import {
  Stack,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  SwitchProps,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Checkbox,
  Chip,
} from "@mui/material";
import { styled } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import { globalTheme } from "../../styles";
import { IS_AUTO_PRINT, Operators } from "../../constants";
import { useGetCarrierImage, usePrinter, useSearchOrderToPrint, useUpdateAction } from "../../hooks";
import { PrintByItemValidationModalContent } from "./PrintByItemValidationModalContent";
import { CustomSnackBar } from "../Snackbar";

export const PrintByItem = ({ bundleView, refreshContent }) => {
  const [searchText, setSearchText] = useState<string>("");
  const [isAutoScanningValidations, setIsAutoScanningValidations] = useState<boolean>(
    JSON.parse(localStorage.getItem(IS_AUTO_PRINT)) || false
  );
  const [enableSearchOrderApiCall, setEnableSearchOrderApiCall] = useState<boolean>(false);
  const [openValidationsModal, setOpenValidationsModal] = useState<boolean>(false);
  const [validationSelectedOrderKeys, setValidationSelectedOrderKeys] = useState([]);
  const [validatingItemsData, setValidatingItemsData] = useState();
  const [selectedOrderKeys, setSelectedOrderKeys] = useState([]);
  const [allowBulkPrint, setAllowBulkPrint] = useState<boolean>(false);
  const [bulkPrintingItems, setBulkPrintingItems] = useState([]);
  const [barcodeFlag, setBarcodeFlag] = useState<boolean>(false);
  const textFieldRef = useRef(null);
  const [snackBar, setSnackBar] = useState<{ open?: boolean; message?: string; severity?: string }>({
    open: false,
    message: "",
    severity: "",
  });
  const [ordersLoaded, setOrdersLoaded] = useState(0);
  const [ordersSelected, setOrdersSelected] = useState(0);
  const [actionUpdateRequest, setActionUpdateRequest] = useState({ requestAvailable: false, request: [] });
  const handleCloseSnackbar = () => {
    setSnackBar({ open: false });
  };
  const [bundleMappedData, setBundleMappedData] = useState([]);
  const [selectAllControlState, setSelectAllControlState] = useState({ controlText: "select all", allSelected: false });

  const CustomSwitch = styled((props: SwitchProps) => <Switch {...props} />)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: globalTheme.blue,
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: globalTheme.blue,
    },
  }));

  const {
    searchOrderToPrintResponse,
    fetchingSearchOrderToPrint,
    errorFetchingSearchOrderToPrint,
    fetchSearchOrderToPrintError,
    refetchSearchOrderToPrint,
  } = useSearchOrderToPrint({
    searchOptions: {
      filters: [
        { field: "shippingStatus", operator: Operators.EQ, value: "ReadyToPrint" },
        { field: "pickingStatus", operator: Operators.EQ, value: "Picked" },
        { field: "issueStatus", operator: Operators.EQ, value: "NoIssue" },
        { field: "pickingDetail.packingStationId", operator: Operators.NE, value: "M" },
      ],
      pageNumber: 1,
      pageSize: 20,
      searchText: searchText,
      searchType: barcodeFlag ? "BARCODE" : "PRODUCT",
    },
    enabled: enableSearchOrderApiCall,
  });

  useEffect(() => {
    if (searchOrderToPrintResponse?.orders && searchOrderToPrintResponse?.orders?.length > 0) {
      const ordersMissingShipmentDetails = searchOrderToPrintResponse.orders.filter(
        (order) => !order.shipmentDetails || order.shipmentDetails.length < 1
      );
      if (ordersMissingShipmentDetails.length > 0) {
        const missingOrderNumbers = ordersMissingShipmentDetails.map((order) => order._id).join(", ");
        setSnackBar({
          message: `Shipment details missing for order Ids: ${missingOrderNumbers}. Please contact support.`,
          severity: "error",
          open: true,
        });
      } else {
        setSelectedOrderKeys([]);
        setBundleMappedData(() => transformOrder(searchOrderToPrintResponse?.orders, bundleView));
        setOrdersLoaded(searchOrderToPrintResponse?.orders?.length);
      }
    } else if (searchOrderToPrintResponse && searchOrderToPrintResponse?.message) {
      setSnackBar({
        message: searchOrderToPrintResponse?.message,
        severity: "error",
        open: true,
      });
    }
  }, [searchOrderToPrintResponse, bundleView]);

  const handleSearchSubmit = () => {
    if (searchText) {
      if (enableSearchOrderApiCall) {
        refetchSearchOrderToPrint();
      } else {
        const isNumeric = /^[0-9]+$/.test(searchText);
        const isValidLength = searchText.length === 12 || searchText.length === 13;
        const isBarcode = isNumeric && isValidLength;
        if (isBarcode) {
          setBarcodeFlag(isBarcode);
          setIsAutoScanningValidations(JSON.parse(localStorage.getItem(IS_AUTO_PRINT)) || false);
        } else {
          localStorage.setItem(IS_AUTO_PRINT, JSON.stringify(false));
          setIsAutoScanningValidations(false);
          setEnableSearchOrderApiCall(true);
        }
      }
    }
  };

  const calculateTotalShippingCost = (items) => {
    const totalItemWiseCost = items.map((item) => item.quantity * item.unitEstimatedShippingCost).reduce((a, b) => a + b, 0);
    return totalItemWiseCost;
  };

  const calculateActualShippingCost = (shipments) => {
    const totalShippingCost = shipments.map((shipment) => shipment.ShippingCost || 0).reduce((a, b) => a + b, 0);
    return totalShippingCost;
  };

  const handleOrderItemClick = (id) => {
    setValidationSelectedOrderKeys((prevState) => {
      if (prevState.includes(id)) {
        // Remove the ID if it's already in the array
        return prevState.filter((item) => item !== id);
      } else {
        // Add the ID if it's not in the array
        return [...prevState, id];
      }
    });
  };

  const handleCheckboxChange = (id, checked) => {
    setSelectedOrderKeys((prevSelectedOrderKeys) => {
      if (checked) {
        // Add id if not already in the array
        return prevSelectedOrderKeys.includes(id) ? prevSelectedOrderKeys : [...prevSelectedOrderKeys, id];
      } else {
        // Remove id if it's in the array
        return prevSelectedOrderKeys.filter((key) => key !== id);
      }
    });
  };

  useEffect(() => {
    if (searchText) {
      setEnableSearchOrderApiCall(true);
      refetchSearchOrderToPrint();
    }
  }, [barcodeFlag, isAutoScanningValidations]);

  useEffect(() => {
    if (searchOrderToPrintResponse && Boolean(searchOrderToPrintResponse?.length)) {
      setBundleMappedData(() => transformOrder(searchOrderToPrintResponse, bundleView));
    }
  }, [searchOrderToPrintResponse?.length, bundleView]);

  useEffect(() => {
    if (!snackBar.open && enableSearchOrderApiCall) {
      refreshContent();
    }
  }, [snackBar.open]);

  useEffect(() => {
    if (errorFetchingSearchOrderToPrint) {
      setSnackBar({
        message: fetchSearchOrderToPrintError?.error?.message,
        severity: "error",
        open: true,
      });
    }
  }, [errorFetchingSearchOrderToPrint]);

  useEffect(() => {
    const transformedOrders = transformOrder(searchOrderToPrintResponse?.orders, bundleView);
    const selectedOrders = selectedOrderKeys.map((id) => transformedOrders[id]);
    setBulkPrintingItems(selectedOrders);
    setOrdersSelected(selectedOrders?.length);

    const hasMoreThanOneQty = selectedOrders.some((order) => order.items.some((item) => item.quantity > 1));
    if (selectedOrderKeys.length > 0 && !hasMoreThanOneQty) {
      setAllowBulkPrint(true);
    } else {
      setAllowBulkPrint(false);
    }
  }, [selectedOrderKeys, bundleView, bundleMappedData]);

  const { successfullUpdatedAction } = useUpdateAction({
    updateActionRequest: actionUpdateRequest.request,
    enabled: actionUpdateRequest.requestAvailable,
  });

  useEffect(() => {
    if (successfullUpdatedAction) {
      onValidation();
    }
  }, [successfullUpdatedAction]);

  const handleBulkPrinting = () => {
    bulkPrintingItems.map((items) => {
      items.shipmentDetails.map((shipmentDetail) => usePrinter(shipmentDetail.pdfLink, shipmentDetail.courierName));
    });

    const actionUpdateRequest = prepareActionUpdateRequest(bulkPrintingItems);
    setActionUpdateRequest({ requestAvailable: true, request: actionUpdateRequest });
  };

  const prepareActionUpdateRequest = (items) => {
    const actionUpdateRequests = items.map((item) => {
      const itemValidatedList = item?.items.map((i) => {
        return { sku: i.sku, allQtsScanned: false, skuManuallyChecked: true };
      });

      return {
        itemValidatedList: itemValidatedList,
        actionType: "PRINTED",
        idList: item.orderIds,
        changes: [{ fieldName: "shippingStatus", oldValue: "", newValue: "PickAndPack" }],
      };
    });
    return actionUpdateRequests;
  };

  useEffect(() => {
    setValidatingItemsData(validationSelectedOrderKeys.map((id) => bundleMappedData[id])[0]);
  }, [validationSelectedOrderKeys]);

  const handleValidationModalClose = () => {
    setOpenValidationsModal(false);
    setValidationSelectedOrderKeys([]);
    setValidatingItemsData(undefined);
  };

  const onValidation = () => {
    setSnackBar({
      message: "Order is successfully moved to Pick and Pack",
      severity: "success",
      open: true,
    });
    return;
  };

  useEffect(() => {
    if (Boolean(bundleMappedData?.length > 0) && isAutoScanningValidations && barcodeFlag) {
      if (bundleMappedData[0].items?.length === 1 && bundleMappedData[0].items[0].quantity === 1) {
        bundleMappedData[0]?.shipmentDetails?.map((shipmentDetail) =>
          usePrinter(shipmentDetail.pdfLink, shipmentDetail.courierName)
        );
        const actionUpdateRequest = prepareActionUpdateRequest([bundleMappedData[0]]);
        setActionUpdateRequest({ requestAvailable: true, request: actionUpdateRequest });
      } else {
        handleOrderItemClick(0);
        setOpenValidationsModal(true);
      }
    }
  }, [bundleMappedData, bundleView, isAutoScanningValidations]);

  const handleSelectAll = () => {
    if (!selectAllControlState.allSelected) {
      setSelectedOrderKeys(Array.from({ length: bundleMappedData?.length }, (_, index) => index));
      setSelectAllControlState({ controlText: "unselect all", allSelected: true });
      setOrdersSelected(bundleMappedData?.length);
    } else {
      setSelectedOrderKeys([]);
      setSelectAllControlState({ controlText: "select all", allSelected: false });
      setOrdersSelected(0);
    }
  };

  return (
    <Stack
      spacing={2}
      marginTop={5}
      height={"75vh"}
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSearchSubmit();
      }}
      overflow="scroll"
    >
      {snackBar.open && (
        <CustomSnackBar
          open={snackBar.open}
          message={snackBar.message}
          autoHideDuration={snackBar.severity === "success" ? 800 : 1200}
          onClose={() => setSnackBar({ open: false, message: "", severity: "" })}
          severity={snackBar.severity}
        />
      )}
      <Stack direction="row" justifyContent="space-between">
        <TextField
          id="standard-helperText"
          label={"Search order by Barcode or Product"}
          variant="standard"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
          inputRef={textFieldRef}
          autoFocus
          sx={{
            marginRight: "24px",
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearchSubmit}
          sx={{
            width: "300px",
          }}
        >
          Search
        </Button>
      </Stack>

      <Stack direction="row" spacing={3} alignItems="center">
        <FormControlLabel
          disabled={Array.isArray(searchOrderToPrintResponse) && searchOrderToPrintResponse.length > 0}
          control={
            <CustomSwitch
              checked={isAutoScanningValidations}
              onChange={() => {
                localStorage.setItem(IS_AUTO_PRINT, JSON.stringify(!isAutoScanningValidations));
                setIsAutoScanningValidations(!isAutoScanningValidations);
              }}
            />
          }
          label={
            <Typography variant="caption" color={globalTheme.blue}>
              Auto Print
            </Typography>
          }
        />
        <Button
          onClick={handleSelectAll}
          variant="outlined"
          disabled={!searchOrderToPrintResponse?.orders || Boolean(!searchOrderToPrintResponse?.orders?.length)}
        >
          {selectAllControlState.controlText}
        </Button>
        <Button onClick={handleBulkPrinting} variant="outlined" disabled={!allowBulkPrint} color="success">
          Bulk print
        </Button>
      </Stack>

      <Stack direction="row" spacing={2}>
        <Chip label={`Total Orders: ${ordersLoaded || 0}`} variant="outlined" />
        <Chip label={`Orders Selected: ${ordersSelected || 0}`} variant="outlined" color="primary" />
      </Stack>

      {fetchingSearchOrderToPrint && <LinearProgress />}

      {bundleMappedData &&
        Boolean(bundleMappedData?.length) &&
        bundleMappedData?.map((item, id) => {
          return (
            <Stack direction="row" spacing={1} key={id} alignItems="flex-start">
              <Checkbox onChange={(e) => handleCheckboxChange(id, e.target.checked)} checked={selectedOrderKeys.includes(id)} />

              <Stack
                width="100%"
                onClick={() => {
                  handleOrderItemClick(id);
                  setOpenValidationsModal(true);
                }}
                sx={{
                  backgroundColor: globalTheme.background,
                  borderRadius: "8px",
                  padding: 2,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1">Id:</Typography>
                      <Typography variant="subtitle1" color={globalTheme.primaryColor}>
                        {item.orderIds.join(", ")}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1">Total price:</Typography>
                      <Typography variant="subtitle1" color={globalTheme.primaryColor}>
                        {item?.totalPrice}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1">Est shipping cost:</Typography>
                      <Typography variant="subtitle1" color={globalTheme.primaryColor}>
                        {calculateTotalShippingCost(item?.items)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1">Actual shipping cost:</Typography>
                      <Typography variant="subtitle1" color={globalTheme.primaryColor}>
                        {calculateActualShippingCost(item?.shipmentDetails)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {item.shipmentDetails.map((shipment, key) => {
                    return (
                      <Stack
                        spacing={1}
                        key={`shipping-entry-${key}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {useGetCarrierImage(shipment.courierName) || (
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="caption">Carrier:</Typography>
                              <Typography variant="caption" color={globalTheme.primaryColor}>
                                {shipment.courierName}
                              </Typography>
                            </Stack>
                          )}
                          <Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="caption">{`Shipping id: `}</Typography>
                              <Typography
                                variant="caption"
                                color={globalTheme.primaryColor}
                              >{`${shipment.shipmentId}`}</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="caption">{`Shipping cost: `}</Typography>
                              <Typography variant="caption" color={globalTheme.primaryColor}>{`${
                                shipment.ShippingCost || "N/A"
                              }`}</Typography>
                            </Stack>

                            <Typography variant="caption">{`Tracking numbers: `}</Typography>
                            {shipment?.shippedItems ? (
                              shipment?.shippedItems?.map((shippedItem, key) => (
                                <Stack key={key}>
                                  <Typography variant="caption" color={globalTheme.primaryColor}>
                                    {shippedItem?.itemTrackingId}
                                  </Typography>
                                </Stack>
                              ))
                            ) : (
                              <Typography variant="caption" color={globalTheme.primaryColor}>
                                N/A
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
                {item.items?.map((i, key) => {
                  return (
                    <Stack direction="row" justifyContent="space-between" key={key}>
                      <Stack direction="row" spacing={2} alignItems="center" margin={0.5}>
                        <img src={i?.imageURL} width="75px" height="75px" alt="product" />
                        <Stack>
                          <Typography variant="subtitle1">{i?.title}</Typography>
                          <Typography variant="caption" color={globalTheme.primaryColor}>
                            {i?.varyingAttributes}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="caption">SKU:</Typography>
                            <Typography variant="caption" color={globalTheme.primaryColor}>
                              {i?.sku}
                            </Typography>
                            <Typography variant="caption">Quantity:</Typography>
                            <Typography variant="h6" color={globalTheme.orange} fontWeight="bold">
                              {i?.quantity}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          );
        })}

      {Boolean(validatingItemsData) && Boolean(validationSelectedOrderKeys.length) && (
        <Dialog
          open={openValidationsModal}
          onClose={handleValidationModalClose}
          aria-labelledby="validations-dialog"
          aria-describedby="validations-dialog"
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <DialogTitle>
            <Typography variant="caption">Validate & print items</Typography>
          </DialogTitle>
          <PrintByItemValidationModalContent
            validatingItems={validatingItemsData}
            onValidation={onValidation}
            onError={handleValidationModalClose}
            onClose={handleValidationModalClose}
            manualShipmentsData={null}
            firstItemBarcode={isAutoScanningValidations ? searchText : ""}
          />
          <DialogActions>
            <Button onClick={handleValidationModalClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
};

const transformOrder = (items, bundleView) => {
  let newItems = structuredClone(items);
  newItems?.map((item) => {
    const mappedItemsData = item.items
      ?.map((i) => {
        let data1 = bundleView ? [i] : i.bundleComponents.length ? i.bundleComponents : [i];
        return data1;
      })
      .flat();

    item.items = mappedItemsData;
    return item;
  });

  return newItems;
};
